import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from "@mui/material";
import LayerControlItem from "./LayerControlItem";
import { useAuth } from "../../contexts/AuthContext";
import { isViewOnly } from "../../utils/permissions";

interface ProjectAreasControlProps {
  /** Whether the project boundary polygons are currently drawn on the map. */
  visible: boolean;
  onToggleVisibility: () => void;
  /** Number of project areas on the map; 0 disables the visibility toggle. */
  projectCount: number;
  /** True while the map is waiting for the user to click a location. */
  pickingLocation: boolean;
  /** Absent while the listing is browsing story maps: no project is created from there. */
  onStartPickLocation?: () => void;
  onCancelPickLocation: () => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * The drawn projects' property boundaries ride along with the project
   * areas; this checkbox lets them be switched off on their own. Omit to
   * hide the control.
   */
  propertiesVisible?: boolean;
  onTogglePropertiesVisible?: () => void;
  /** Parcels drawn, and how many projects they belong to. */
  propertyCount?: number;
  propertyProjectCount?: number;
  /** Some projects had more parcels than the server sends. */
  propertiesTruncated?: boolean;
  propertiesLoading?: boolean;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * The project-listing map's project layer control: toggles the project area
 * polygons (with their property boundaries), and hosts the "Create New
 * Project" action that puts the map into location-pick mode.
 */
const ProjectAreasControl: React.FC<ProjectAreasControlProps> = ({
  visible,
  onToggleVisibility,
  projectCount,
  pickingLocation,
  onStartPickLocation,
  onCancelPickLocation,
  expanded,
  onExpandedChange,
  propertiesVisible = true,
  onTogglePropertiesVisible,
  propertyCount = 0,
  propertyProjectCount = 0,
  propertiesTruncated = false,
  propertiesLoading = false,
}) => {
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);

  const propertiesContent = onTogglePropertiesVisible ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <FormControlLabel
        sx={{ ml: -0.5, mr: 0 }}
        control={
          <Checkbox
            size="small"
            checked={propertiesVisible}
            onChange={onTogglePropertiesVisible}
            disabled={!visible}
            sx={{ p: 0.5, color: "#94a3b8", "&.Mui-checked": { color: "#00d4ad" } }}
          />
        }
        label={
          <Typography variant="body2" sx={{ color: visible ? "#e2e8f0" : "#64748b", fontSize: "0.8rem" }}>
            Property boundaries
          </Typography>
        }
      />
      <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem", lineHeight: 1.4, pl: 3.5 }}>
        {!visible || !propertiesVisible ? (
          "Hidden."
        ) : propertiesLoading ? (
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <CircularProgress size={10} sx={{ color: "#94a3b8" }} /> Loading…
          </Box>
        ) : propertyCount === 0 ? (
          "None of the projects on the map has property boundaries."
        ) : (
          `${plural(propertyCount, "parcel")} across ${plural(propertyProjectCount, "project")}` +
          `${propertiesTruncated ? ", trimmed for the largest" : ""}. Shown once you zoom in.`
        )}
      </Typography>
    </Box>
  ) : null;

  const createContent = viewOnly || !onStartPickLocation ? null : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={pickingLocation ? onCancelPickLocation : onStartPickLocation}
        sx={{
          color: pickingLocation ? "#faad17" : "#00d4ad",
          borderColor: pickingLocation ? "#faad17" : "#00d4ad",
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: pickingLocation
              ? "rgba(250, 173, 23, 0.1)"
              : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        {pickingLocation ? "Cancel" : "Create New Project"}
      </Button>
      <Typography
        variant="caption"
        sx={{ color: "#94a3b8", fontSize: "0.7rem", lineHeight: 1.4 }}
      >
        {pickingLocation
          ? "Click anywhere on the map to place the new project. You can fine-tune the location in the wizard."
          : "Pick the location straight off the map, then finish setting the project up in the wizard."}
      </Typography>
    </Box>
  );

  const drawingContent =
    propertiesContent || createContent ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {propertiesContent}
        {createContent}
      </Box>
    ) : null;

  return (
    <LayerControlItem
      title="Project Areas"
      icon="layers"
      visible={visible}
      onToggleVisibility={onToggleVisibility}
      enabled={projectCount > 0}
      drawingContent={drawingContent}
      drawingContentLabel="Projects"
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    />
  );
};

export default ProjectAreasControl;
